import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Building2, DollarSign, TrendingUp, Loader2, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getCurrentFloorPlan, getFloorPlanStats, getLinkedDealers } from "@/Calls/callFloorPlanAuth";

interface Stats {
    active_dealers: number; total_credit_extended: number;
    total_available_credit: number; total_inventory_count: number;
}
interface Company { company_name: string; email: string; interest_rate?: string; credit_limit_total?: number; available_credit_total?: number; }
interface DealerLink {
    id: number; dealer_id: number; credit_limit: number; available_credit: number;
    status: string; interest_rate: string | null;
    dealer: { dealer_name: string; dealer_phone?: string } | null;
}

export default function FloorPlanDashboard() {
    const [company, setCompany] = useState<Company | null>(null);
    const [stats,   setStats]   = useState<Stats | null>(null);
    const [dealers, setDealers] = useState<DealerLink[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentFloorPlan().then((r) => setCompany(r.data.company ?? r.data)),
            getFloorPlanStats().then((d)    => setStats(d.stats)),
            getLinkedDealers().then((d)     => setDealers((d.dealers ?? []).slice(0, 6))),
        ])
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    const fmt = (n: number) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Floor Plan Dashboard</h1>
                <p className="text-muted-foreground text-sm mt-0.5">{company?.company_name}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: "Active Dealers",      value: stats?.active_dealers ?? 0,                       icon: Building2,  color: "text-emerald-600", bg: "bg-emerald-50" },
                    { label: "Total Credit",         value: fmt(stats?.total_credit_extended ?? 0),           icon: CreditCard, color: "text-blue-600",    bg: "bg-blue-50"    },
                    { label: "Available Credit",     value: fmt(stats?.total_available_credit ?? 0),          icon: DollarSign, color: "text-violet-600",  bg: "bg-violet-50"  },
                    { label: "Inventory Financed",   value: stats?.total_inventory_count ?? 0,                icon: TrendingUp, color: "text-orange-600",  bg: "bg-orange-50"  },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">{value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Dealer Credit Lines */}
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Dealer Credit Lines</h2>
                    <Link to="/floorplan/dealers" className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                        View all <ArrowUpRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="divide-y">
                    {dealers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No linked dealers yet</p>
                    ) : dealers.map((d) => {
                        const used = d.credit_limit - d.available_credit;
                        const pct  = d.credit_limit > 0 ? (used / d.credit_limit) * 100 : 0;
                        return (
                            <div key={d.id} className="px-5 py-3 hover:bg-muted/40">
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <p className="text-sm font-medium">{d.dealer?.dealer_name ?? `Dealer #${d.dealer_id}`}</p>
                                        {d.interest_rate && (
                                            <p className="text-xs text-muted-foreground">{d.interest_rate}% APR</p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-mono font-medium">{fmt(d.available_credit)} available</p>
                                        <p className="text-xs text-muted-foreground">of {fmt(d.credit_limit)} total</p>
                                    </div>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${pct > 80 ? "bg-red-500" : pct > 60 ? "bg-orange-500" : "bg-emerald-500"}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{pct.toFixed(0)}% utilized</p>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* Company Info */}
            {company && (
                <Card className="p-5">
                    <h2 className="font-semibold mb-3">Account Info</h2>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-muted-foreground">Email:</span> <span className="ml-2">{company.email}</span></div>
                        {company.interest_rate && (
                            <div><span className="text-muted-foreground">Base Rate:</span> <span className="ml-2">{company.interest_rate}%</span></div>
                        )}
                    </div>
                </Card>
            )}
        </div>
    );
}
